import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response.model';
import { SessionProfile } from '../models/session.model';
import { Role } from '../constants/roles';

const STORAGE_KEY = 'bo_session_profile';

/**
 * Holds the authenticated BackOffice profile.
 *
 * The login endpoint returns only { id, name, email, role }; the full
 * profile is fetched from GET /auth/me.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  private readonly profile$ = new BehaviorSubject<SessionProfile | null>(this.restore());

  /** Observable of the current profile. */
  get profile(): Observable<SessionProfile | null> {
    return this.profile$.asObservable();
  }

  get current(): SessionProfile | null {
    return this.profile$.value;
  }

  get role(): Role | null {
    return this.profile$.value?.role ?? null;
  }

  /** Fetch the full profile from GET /auth/me and cache it. */
  loadProfile(): Observable<SessionProfile | null> {
    return this.http.get<ApiResponse<SessionProfile>>(`${this.apiUrl}auth/me`).pipe(
      map((res) => res.data),
      tap((profile) => this.setProfile(profile)),
      catchError(() => of(this.profile$.value)),
    );
  }

  setProfile(profile: SessionProfile | null): void {
    this.profile$.next(profile);
    if (profile) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  clear(): void {
    this.setProfile(null);
  }

  private restore(): SessionProfile | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as SessionProfile) : null;
    } catch {
      return null;
    }
  }
}
